import { useEffect, useRef, useState } from 'react';
import { useIsAdmin } from '@/lib/hooks';
import { closeSheet } from '@/lib/overlays';
import { navigate, useRoute, type RecordRef } from '@/lib/router';
import { Slot } from '@/lib/slots';
import { Empty } from '@/ui/bits';
import { Sheet } from '@/ui/Sheet';
import { ViewBoundary } from './ViewHost';

// The two sheets that slide up over the current view: a person or company record, and the admin console.
// Both follow the address bar, so links, refreshes and the back button open and close them.

export function RecordSheetHost() {
  const { record } = useRoute();
  // Keeps showing the last record while the sheet slides away.
  const [shown, setShown] = useState<RecordRef | null>(record);
  const bodyRef = useRef<HTMLDivElement>(null);
  if (record && (record.type !== shown?.type || record.id !== shown.id || record.tab !== shown.tab || record.focus !== shown.focus)) {
    setShown(record);
  }
  const current = record ?? shown;
  const identity = current ? `${current.type}/${current.id}` : '';

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: 0 });
  }, [identity]);

  return (
    <Sheet
      open={!!record}
      onClose={closeSheet}
      kind="record"
      id="sl-record"
      label={current?.type === 'company' ? 'Company record' : 'Person record'}
      closeLabel="Close record"
      bodyRef={bodyRef}
    >
      {current ? (
        <ViewBoundary key={identity} label="This record">
          <Slot
            name="record.sheet"
            record={current}
            fallback={<Empty icon="user" title="Records are not available" />}
          />
        </ViewBoundary>
      ) : null}
    </Sheet>
  );
}

export function AdminSheetHost() {
  const { admin } = useRoute();
  const isAdmin = useIsAdmin();
  const [section, setSection] = useState(admin);
  if (admin && admin !== section) setSection(admin);

  // The console is for the super admin; anyone else following a link to it stays where they are.
  useEffect(() => {
    if (admin && !isAdmin) navigate({ admin: null }, { replace: true });
  }, [admin, isAdmin]);

  const open = !!admin && isAdmin;
  return (
    <Sheet open={open} onClose={closeSheet} kind="admin" full id="sl-admin" label="Admin console" closeLabel="Close admin console">
      {section && isAdmin ? (
        <ViewBoundary label="The admin console">
          <Slot name="admin.console" section={section} fallback={<Empty icon="shield" title="The admin console is not available" />} />
        </ViewBoundary>
      ) : null}
    </Sheet>
  );
}
